export const userHasValidRolesForNavItem = (navItem, user) => {
  if (navItem?.hideFromRestrictedSegmentUser && user?.restrictedSavedSegmentId) return false

  if (!navItem?.roles) return true

  const roles = user?.roles || []

  return navItem.roles.some((r) => roles.includes(r))
}

export const getNavigationForUser = (user, channels, opts = {}) => {
  const internalUser = opts?.internalUser
  const enabledChannels = channels?.filter((ch) => ch.enabled).map((ch) => ch.advertisingChannelId) || []
  const shouldShow = (link) => {
    if (!user.capabilities) return false
    if (link.channels) {
      let channelMatches = 0
      for (const channel of link.channels) {
        if (enabledChannels.includes(channel)) {
          channelMatches++
        }
      }
      if (channelMatches === 0) {
        return false
      }
    }
    if (link.capabilities) {
      for (const capability of link.capabilities) {
        if (!user.capabilities[capability]) {
          return false
        }
      }
    }

    if (!userHasValidRolesForNavItem(link, user)) {
      return false
    }

    if (link.persona && user.capabilities[link.persona] === 'OFF') {
      return false
    }
    if (link.internalUser && !internalUser) {
      return false
    }
    return true
  }

  return {
    navigation: BionicNavigation.filter(shouldShow)
  }
}

export const BionicNavigation = [
  // *******************************************************************************************************************
  // MANAGE ************************************************************************************************************
  // *******************************************************************************************************************
  {
    link: '/manage/',
    text: 'Accounts',
    roles: ['user'],
    capabilities: ['Manage'],
    shortkey: ['meta', 'ctrl', 'shift', 'm'],
    persona: 'persona_manage_basic',
    qa: 'manage',
    tracking: 'manage',
    placements: [
      {
        top: 'manage',
        sub: 'portfolio',
        primary: true
      }
    ]
  },
  {
    link: '/bulk/',
    text: 'Bulk Manage',
    roles: ['user'],
    capabilities: ['BulkOperations'],
    persona: 'persona_manage_bulkActions',
    qa: 'bulkEdit',
    tracking: 'bulkEdit',
    placements: [
      {
        top: 'manage',
        sub: 'bulkManage'
      }
    ]
  },
  {
    text: 'Bulk Receipts',
    link: '/bulk/receipt/',
    roles: ['user'],
    capabilities: ['BulkOperations'],
    persona: 'persona_manage_bulkActions',
    qa: 'bulkReceipts',
    tracking: 'bulkReceipts',
    placements: [
      {
        top: 'manage',
        sub: 'bulkManage'
      }
    ]
  },
  {
    text: 'Performance Bounds',
    link: '/settings/customer/?customerForm=performanceBounds',
    roles: ['manager', 'user-manager'],
    hideFromRestrictedSegmentUser: true,
    qa: 'performanceBounds',
    tracking: 'performanceBounds',
    placements: [
      {
        top: 'manage',
        sub: 'tools'
      }
    ]
  },
  {
    text: 'Target Sets',
    link: '/settings/target-sets/',
    roles: ['manager', 'admin'],
    channels: [29, 30, 32],
    qa: 'targetSets',
    tracking: 'targetSets',
    placements: [
      {
        top: 'manage',
        sub: 'tools'
      }
    ]
  },
  {
    text: 'Conversion Actions',
    link: '/settings/customer/?customerForm=conversionActions',
    roles: ['manager'],
    hideFromRestrictedSegmentUser: true,
    qa: 'conversionActions',
    tracking: 'conversionActions',
    placements: [
      {
        top: 'manage',
        sub: 'tools'
      }
    ]
  },
  {
    text: 'Budget Rollover',
    link: '/insights/budget-adjustments/',
    beta: true,
    roles: ['manager'],
    hideFromRestrictedSegmentUser: true,
    qa: 'budgetRollover',
    tracking: 'budgetRollover',
    placements: [
      {
        top: 'manage',
        sub: 'tools'
      }
    ]
  },
  {
    text: 'DSP Macros',
    link: '/settings/customer/?customerForm=dspMacros',
    roles: ['user', 'manager', 'admin'],
    channels: [29, 30, 32],
    qa: 'dspMacros',
    tracking: 'dspMacros',
    placements: [
      {
        top: 'manage',
        sub: 'tools'
      }
    ]
  },
  {
    text: 'Data Lenses',
    link: '/settings/data-lenses/',
    roles: ['manager', 'admin'],
    internalUser: true,
    qa: 'dataLenses',
    tracking: 'dataLenses',
    placements: [
      {
        top: 'manage',
        sub: 'tools'
      }
    ]
  },
  {
    text: 'Shared Audiences',
    link: '/settings/shared-audiences/',
    roles: ['user-manager', 'manager', 'admin'],
    hideFromRestrictedSegmentUser: true,
    capabilities: ['Manage', 'CustomerSharedAudiences'],
    qa: 'sharedAudiences',
    tracking: 'sharedAudiences',
    placements: [
      {
        top: 'manage',
        sub: 'tools'
      }
    ]
  },
  {
    text: 'Creative Approval',
    link: '/approvals/',
    capabilities: ['VideoTemplates'],
    qa: 'creativeApproval',
    tracking: 'creativeApproval',
    placements: [
      {
        top: 'manage',
        sub: 'tools'
      }
    ]
  },
  {
    text: 'CoLab Hub',
    link: '/colab/',
    qa: 'colabHub',
    tracking: 'colabHub',
    hideFromRestrictedSegmentUser: true,
    capabilities: ['CoLab'],
    placements: [
      {
        top: 'manage',
        sub: 'colab'
      }
    ]
  },
  // *******************************************************************************************************************
  // AUTOMATE **********************************************************************************************************
  // *******************************************************************************************************************
  {
    link: '/blueprints/',
    text: 'Blueprint Editor',
    roles: ['user'],
    capabilities: ['BlueprintEditor'],
    shortkey: ['meta', 'ctrl', 'shift', 'b'],
    persona: 'persona_blueprints_view',
    qa: 'blueprints',
    tracking: 'blueprints',
    placements: [
      {
        top: 'automate',
        sub: 'blueprints',
        primary: true
      }
    ]
  },
  {
    text: 'Tag Tester',
    link: '/blueprints/tag-review/',
    roles: ['manager'],
    capabilities: ['BlueprintEditor'],
    persona: 'persona_blueprints_view',
    qa: 'tagTester',
    tracking: 'tagTester',
    placements: [
      {
        top: 'automate',
        sub: 'blueprints'
      }
    ]
  },
  {
    link: '/launch/',
    text: 'Launch',
    roles: ['user'],
    capabilities: ['Blueprints'],
    childPath: '/launch/',
    shortkey: ['meta', 'ctrl', 'shift', 'l'],
    persona: 'persona_launch_view',
    qa: 'launch',
    tracking: 'launch',
    placements: [
      {
        top: 'automate',
        sub: 'launch'
      }
    ]
  },
  {
    link: '/launch/status/',
    text: 'Launch Status',
    roles: ['user'],
    capabilities: ['Blueprints'],
    persona: 'persona_launch_view',
    qa: 'launchStatus',
    tracking: 'launchStatus',
    placements: [
      {
        top: 'automate',
        sub: 'launch'
      }
    ]
  },
  {
    text: 'Account Ingest',
    link: '/settings/account-ingest/',
    roles: ['user-manager', 'manager', 'admin'],
    hideFromRestrictedSegmentUser: true,
    qa: 'accountIngest',
    tracking: 'accountIngest',
    placements: [
      {
        top: 'automate',
        sub: 'blueprintData'
      }
    ]
  },
  {
    text: 'Data Sources',
    link: '/settings/customer/?customerForm=dataSources',
    roles: ['manager', 'user-manager'],
    hideFromRestrictedSegmentUser: true,
    qa: 'dataSources',
    tracking: 'dataSources',
    placements: [
      {
        top: 'automate',
        sub: 'blueprintData'
      }
    ]
  },
  {
    text: 'Catalog Templates',
    link: '/settings/customer/?customerForm=inventoryCatalogs',
    roles: ['manager', 'user-manager'],
    capabilities: ['Blueprints'],
    qa: 'inventoryCatalogs',
    tracking: 'inventoryCatalogs',
    placements: [
      {
        top: 'automate',
        sub: 'blueprintData'
      }
    ]
  },
  {
    text: 'Form Designer',
    link: '/form-designer/',
    qa: 'formDesigner',
    tracking: 'formDesigner',
    internalUser: true,
    capabilities: ['Blueprints', 'CoLab'],
    placements: [
      {
        top: 'automate',
        sub: 'blueprintData'
      }
    ]
  },
  {
    text: 'HTML Ad Templates',
    link: '/html5-ads/',
    roles: ['manager', 'admin'],
    capabilities: ['Blueprints'],
    classes: 'html-ad-templates',
    channels: [29, 14, 15, 16, 17, 18],
    qa: 'htmlAdTemplates',
    tracking: 'htmlAdTemplates',
    placements: [
      {
        top: 'automate',
        sub: 'blueprintData'
      }
    ]
  },
  {
    text: 'Lead Form Templates',
    link: '/settings/customer/?customerForm=leadForms',
    roles: ['manager', 'user-manager'],
    capabilities: ['Blueprints'],
    qa: 'leadForms',
    tracking: 'leadForms',
    placements: [
      {
        top: 'automate',
        sub: 'blueprintData'
      }
    ]
  },
  {
    text: 'Recommendation Policies',
    link: '/settings/customer/?customerForm=recommendationPolicy',
    roles: ['manager', 'user-manager'],
    capabilities: ['BulkOperations'],
    persona: 'persona_notifications_viewRecommendedActionsToMyAccounts',
    qa: 'recommendationPolicies',
    tracking: 'recommendationPolicies',
    placements: [
      {
        top: 'automate',
        sub: 'automationTools'
      }
    ]
  },
  {
    text: 'Budget Reallocation Strategy',
    link: '/settings/customer/?customerForm=budgetReallocation',
    hideFromRestrictedSegmentUser: true,
    qa: 'budgetReallocationStrategy',
    tracking: 'budgetReallocationStrategy',
    placements: [
      {
        top: 'automate',
        sub: 'automationTools'
      }
    ]
  },
  {
    link: '/settings/customer/?customerForm=webProviderAnalysis',
    text: 'Web Provider Analysis',
    roles: ['manager', 'user-manager'],
    capabilities: ['BlueprintEditor'],
    qa: 'webProviderAnalysis',
    tracking: 'webProviderAnalysis',
    placements: [
      {
        top: 'automate',
        sub: 'automationTools'
      }
    ]
  },
  {
    link: '/settings/customer/?customerForm=webhooks',
    text: 'Webhooks',
    roles: ['manager', 'user-manager'],
    capabilities: ['BlueprintEditor'],
    persona: 'persona_settings_developerLevel',
    qa: 'webhooks',
    tracking: 'webhooks',
    placements: [
      {
        top: 'automate',
        sub: 'automationTools'
      }
    ]
  },
  // *******************************************************************************************************************
  // INSIGHTS **********************************************************************************************************
  // *******************************************************************************************************************
  {
    link: '/reporting/data-explorer/',
    text: 'Data Explorer',
    roles: ['user'],
    qa: 'dataExplorer',
    tracking: 'dataExplorer',
    placements: [
      {
        top: 'insights',
        sub: 'reportingTools',
        primary: true
      }
    ]
  },
  {
    link: '/reporting/',
    text: 'Report Designer',
    roles: ['user'],
    persona: 'persona_reports_view',
    qa: 'reporting',
    tracking: 'reporting',
    placements: [
      {
        top: 'insights',
        sub: 'reportingTools'
      }
    ]
  },
  {
    link: '/reporting/history/',
    text: 'Report History',
    roles: ['user'],
    hideFromRestrictedSegmentUser: true,
    qa: 'reportHistory',
    tracking: 'reportHistory',
    placements: [
      {
        top: 'insights',
        sub: 'reportingTools'
      }
    ]
  },
  {
    link: '/alerts/',
    text: 'Notifications',
    roles: ['user'],
    persona: 'persona_notifications_viewRelevantToMyAccounts',
    qa: 'alerts',
    tracking: 'alerts',
    placements: [
      {
        top: 'insights',
        sub: 'monitor'
      }
    ]
  },
  {
    link: '/alerts/anomalies/',
    text: 'Anomalies',
    roles: ['user'],
    persona: 'persona_notifications_viewRelevantToMyAccounts',
    qa: 'alerts',
    tracking: 'alerts',
    placements: [
      {
        top: 'insights',
        sub: 'monitor'
      }
    ]
  },
  {
    link: '/dashboard/',
    text: 'Dashboard',
    roles: ['user'],
    shortkey: ['meta', 'ctrl', 'shift', 'd'],
    qa: 'dashboard',
    tracking: 'dashboard',
    placements: [
      {
        top: 'insights',
        sub: 'monitor'
      }
    ]
  },
  {
    text: 'Keyword Recommendations',
    link: '/insights/keyword-recommendations/',
    roles: ['user'],
    classes: 'keywordRecommendations',
    persona: 'persona_notifications_viewRecommendedActionsToMyAccounts',
    qa: 'keywordRecommendations',
    tracking: 'keywordRecommendations',
    placements: [
      {
        top: 'insights',
        sub: 'monitor'
      }
    ]
  },
  {
    link: '/insights/analyst-scorecard/',
    text: 'Analyst Portfolio Health',
    roles: ['manager'],
    hideFromRestrictedSegmentUser: true,
    qa: 'analystPortfolioHealth',
    tracking: 'analystPortfolioHealth',
    placements: [
      {
        top: 'insights',
        sub: 'monitor'
      }
    ]
  },
  {
    link: '/insights/budget-groups/',
    text: 'Budget Groups',
    roles: ['user'],
    qa: 'budgetGroups',
    tracking: 'budgetGroups',
    capabilities: ['CampaignGroups'],
    placements: [
      {
        top: 'insights',
        sub: 'monitor'
      }
    ]
  },
  {
    link: '/prospecting/',
    text: 'Prospects',
    roles: ['user'],
    beta: true,
    persona: 'persona_manage_budgetRecommendations',
    qa: 'prospects',
    tracking: 'prospects',
    placements: [
      {
        top: 'insights',
        sub: 'monitor'
      }
    ]
  },
  // *******************************************************************************************************************
  // SETTINGS **********************************************************************************************************
  // *******************************************************************************************************************
  {
    link: '/settings/customer/?customerForm=customerInfo',
    text: 'Customer Info',
    icon: 'info',
    roles: ['manager', 'user-manager'],
    persona: 'persona_settings_userAndGlobal',
    qa: 'customerInfo',
    tracking: 'customerInfo',
    placements: [
      {
        top: 'settings',
        sub: 'globalSettings'
      }
    ]
  },
  {
    link: '/settings/customer/?customerForm=accounts',
    text: 'Accounts Management',
    icon: 'accountManagement',
    persona: 'persona_settings_userAndGlobal',
    roles: ['user', 'manager', 'user-manager'],
    hideFromRestrictedSegmentUser: true,
    qa: 'accountManagement',
    tracking: 'accountManagement',
    placements: [
      {
        top: 'settings',
        sub: 'globalSettings'
      }
    ]
  },
  {
    link: '/settings/customer/?customerForm=users',
    text: 'User Management',
    icon: 'userManagement',
    persona: 'persona_settings_userAndGlobal',
    roles: ['user', 'manager', 'user-manager'],
    qa: 'userManagement',
    tracking: 'userManagement',
    placements: [
      {
        top: 'settings',
        sub: 'globalSettings'
      }
    ]
  },
  {
    link: '/setup/link/',
    text: 'Platform Integrations',
    icon: 'integrations',
    roles: ['manager'],
    qa: 'platformIntegrations',
    tracking: 'platformIntegrations',
    placements: [
      {
        top: 'settings',
        sub: 'globalSettings'
      }
    ]
  },
  {
    link: '/setup/checklist/',
    text: 'Onboarding Checklist',
    icon: 'integrations',
    internalUser: true,
    qa: 'setupChecklist',
    tracking: 'setupChecklist',
    placements: [
      {
        top: 'settings',
        sub: 'globalSettings'
      }
    ]
  },
  {
    link: '#',
    text: 'Download SDF (All Accounts)',
    icon: 'download',
    roles: ['user'],
    qa: 'downloadSDF',
    tracking: 'downloadSDF',
    action: 'download-sdf',
    channels: [31],
    placements: [
      {
        top: 'settings',
        sub: 'globalSettings'
      }
    ]
  },
  {
    link: '/reporting/billing/',
    text: 'Billing',
    icon: 'budget',
    internalUser: true,
    qa: 'billing',
    tracking: 'billing',
    placements: [
      {
        top: 'settings',
        sub: 'internalTools'
      }
    ]
  },
  // *******************************************************************************************************************
  // HIDDEN STUFF ******************************************************************************************************
  // *******************************************************************************************************************
  {
    link: '/welcome/',
    text: 'Welcome Page',
    qa: 'welcomePage',
    tracking: 'welcomePage'
  }
]
